/**
 * Where the two tokens live.
 *
 * The access token is kept **in memory only**: it is short-lived and is the thing attached to every
 * request, so keeping it out of storage limits what an XSS payload can walk away with. On a page
 * reload it is simply re-minted from the refresh token.
 *
 * The refresh token is in `localStorage`, which is a deliberate compromise: it survives a reload but
 * is readable by script. The stronger option is an httpOnly cookie, which needs the backend to set
 * and read it — the seam is here so that swap touches one file.
 */

const REFRESH_TOKEN_KEY = 'manufacturing.refreshToken'

let accessToken: string | null = null

export const tokenStorage = {
  getAccessToken(): string | null {
    return accessToken
  },

  setAccessToken(token: string | null): void {
    accessToken = token
  },

  getRefreshToken(): string | null {
    try {
      return window.localStorage.getItem(REFRESH_TOKEN_KEY)
    } catch {
      // Private browsing modes can throw on localStorage access; treat it as "not signed in"
      // rather than breaking the app.
      return null
    }
  },

  setRefreshToken(token: string | null): void {
    try {
      if (token === null) {
        window.localStorage.removeItem(REFRESH_TOKEN_KEY)
      } else {
        window.localStorage.setItem(REFRESH_TOKEN_KEY, token)
      }
    } catch {
      /* ignore: the session then simply does not survive a reload */
    }
  },

  clear(): void {
    accessToken = null
    this.setRefreshToken(null)
  }
}
